import * as React from 'react';
import { Field as FinalField } from 'react-final-form';
import type { FieldState } from 'final-form';
import Field from '@lendi-ui/field';

import { Input, InputProp } from '@lendi-ui/text-input';
import { getAriaLabelById, validateFixedLength, validateNotEmptyString } from '@funnels/utils';
import { FieldLabel } from '../_common/FieldLabel';
import { FieldWrapper } from '../_common/style';
import { CommonFieldProps } from '../_common/types';

interface FieldProps {
  name: string;
  format?: (value: any) => any;
  parse?: (value: any) => any;
  validate?: (value: any, allValues?: Object, FieldMeta?: FieldState<any>) => any;
}
export interface TextInputFieldProps extends CommonFieldProps, FieldProps, Omit<InputProp, 'name' | 'onChange'> {
  showAllowedList?: boolean;
  fixedLength?: number;
  labelAfter?: React.ReactNode;
  className?: string;
}

const TextInputField = (props: TextInputFieldProps) => {
  const {
    after,
    before,
    fixedLength,
    format,
    isDisabled,
    isOptional,
    label,
    labelAfter,
    name,
    parse,
    placeholder,
    subLabel,
    type,
    validate,
    validationFailMessage,
    disableFailMessage = false,
    'aria-labelledby': ariaLabelledby,
    'aria-describedby': ariaDescribedby,
    className,
    ...otherProps
  } = props;
  const fieldProps: FieldProps = {
    name,
    parse,
    format,
  };

  const inputProps = {
    after,
    before,
    isDisabled,
    labelAfter,
    placeholder,
    type,
  };

  if (!isDisabled) {
    if (validate) {
      fieldProps.validate = validate;
    } else if (fixedLength) {
      fieldProps.validate = validateFixedLength(validationFailMessage, fixedLength, isOptional);
    } else {
      fieldProps.validate = validateNotEmptyString(validationFailMessage, isOptional);
    }
  }

  return (
    <FieldWrapper>
      <FieldLabel label={label} labelAfter={labelAfter} subLabel={subLabel} idPostfix={name} isOptional={isOptional} />
      <FinalField {...fieldProps}>
        {({ input, meta }) => (
          <Field {...meta} error={disableFailMessage ? undefined : meta.error}>
            <Input
              {...otherProps}
              data-component={name}
              size="md"
              {...input}
              {...inputProps}
              isDisabled={isDisabled}
              isError={!!(meta.error && meta.touched)}
              isFullWidth
              className={className}
              aria-labelledby={getAriaLabelById('label', label ? name : undefined, ariaLabelledby)}
              aria-describedby={getAriaLabelById('description', subLabel ? name : undefined, ariaDescribedby)}
              aria-invalid={disableFailMessage ? false : meta.error && meta.touched}
            />
          </Field>
        )}
      </FinalField>
    </FieldWrapper>
  );
};

export default TextInputField;
